/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * BaCustomerList — read-only paged customer table. Pager drives limit/skip
 * (server-side); the search box filters the CURRENT page client-side. Row click
 * opens the detail drawer. Uses a native <table> themed by WebAwesome's
 * native.css (WebAwesome ships no table component). Lit light-DOM.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  fetchUserPage,
  AdminForbiddenError,
  AdminUnauthorizedError,
  type AdminUserPage,
  type AdminUserView,
} from '../services/adminApi.js';

export const BA_NOT_AUTHORIZED_EVENT = 'ba-not-authorized';
export const BA_SESSION_EXPIRED_EVENT = 'ba-session-expired';
export const BA_CUSTOMER_SELECTED_EVENT = 'ba-customer-selected';

@customElement('ba-customer-list')
export class BaCustomerList extends LitElement {
  @state() private page: AdminUserPage | null = null;
  @state() private loading = true;
  @state() private failed = false;
  @state() private search = '';
  @state() private limit = 50;
  @state() private skip = 0;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.failed = false;
    try {
      this.page = await fetchUserPage(this.limit, this.skip);
    } catch (error) {
      if (error instanceof AdminForbiddenError) {
        this.emit(BA_NOT_AUTHORIZED_EVENT);
        return;
      }
      if (error instanceof AdminUnauthorizedError) {
        this.emit(BA_SESSION_EXPIRED_EVENT);
        return;
      }
      console.error('Failed to load users:', error);
      this.failed = true;
    } finally {
      this.loading = false;
    }
  }

  private emit(name: string, detail?: unknown): void {
    this.dispatchEvent(
      new CustomEvent(name, { detail, bubbles: true, composed: true })
    );
  }

  private get filtered(): AdminUserView[] {
    const rows = this.page?.users ?? [];
    const q = this.search.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(u =>
      `${u.email} ${u.fullName ?? ''}`.toLowerCase().includes(q)
    );
  }

  override render(): TemplateResult {
    if (this.loading) {
      return html`<wa-spinner data-testid="loading"></wa-spinner>`;
    }
    if (this.failed) {
      return html`
        <wa-callout variant="danger" data-testid="error">
          <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
          Could not load customers.
          <wa-button
            slot="action"
            variant="brand"
            data-testid="retry"
            @click=${() => this.load()}
          >
            Retry
          </wa-button>
        </wa-callout>
      `;
    }
    return html`
      <div class="ba-toolbar">
        <wa-input
          type="search"
          placeholder="Filter this page…"
          data-testid="search"
          .value=${this.search}
          @input=${this.onSearch}
        ></wa-input>
        ${this.renderPager()}
      </div>
      ${this.renderTable()}
      <ba-customer-detail></ba-customer-detail>
    `;
  }

  private renderPager(): TemplateResult {
    const total = this.page?.total ?? 0;
    const from = total === 0 ? 0 : this.skip + 1;
    const to = Math.min(this.skip + this.limit, total);
    const hasPrev = this.skip > 0;
    const hasNext = this.skip + this.limit < total;
    return html`
      <span data-testid="range">${from}–${to} of ${total}</span>
      <wa-button
        size="s"
        data-testid="prev"
        ?disabled=${!hasPrev}
        @click=${this.prevPage}
      >
        Prev
      </wa-button>
      <wa-button
        size="s"
        data-testid="next"
        ?disabled=${!hasNext}
        @click=${this.nextPage}
      >
        Next
      </wa-button>
    `;
  }

  private renderTable(): TemplateResult {
    const rows = this.filtered;
    return html`
      <div class="ba-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Groups</th>
              <th>Auth</th>
              <th>Custodians</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(
              u => html`
                <tr
                  class="ba-row-clickable"
                  data-email=${u.email}
                  @click=${() => this.openDetail(u)}
                >
                  <td>${u.email}</td>
                  <td>${u.fullName ?? ''}</td>
                  <td>
                    ${u.groups.map(g => html`<wa-badge>${g}</wa-badge> `)}
                  </td>
                  <td>
                    ${u.authenticationTypes.map(
                      a => html`<wa-badge variant="neutral">${a}</wa-badge> `
                    )}
                  </td>
                  <td>${u.custodianCount}</td>
                </tr>
              `
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  private onSearch(event: Event): void {
    this.search = (event.target as HTMLInputElement).value ?? '';
  }

  private prevPage(): void {
    this.skip = Math.max(0, this.skip - this.limit);
    void this.load();
  }

  private nextPage(): void {
    this.skip = this.skip + this.limit;
    void this.load();
  }

  private openDetail(user: AdminUserView): void {
    this.emit(BA_CUSTOMER_SELECTED_EVENT, user);
    const detail = this.querySelector('ba-customer-detail') as
      (HTMLElement & { show?: (u: AdminUserView) => void }) | null;
    detail?.show?.(user);
  }
}
